'use client';

import { createContext, ReactNode, useContext, useEffect, useRef } from 'react';
import { create, useStore } from 'zustand';
import { Envs } from '@ae/envs';
import { Locale } from '@ae/i18n';
import { getBeDomain, getForeignDomain } from '../path/helpers';
import { useShallow } from 'zustand/react/shallow';

interface RouterContextState {
  protocol: string;
  hostname: string;
  pathname: string;
  query: string;
  goBack: () => void;
  linkLegacyBehavior: boolean;
  prodBeDomains: { [key in Locale]: string };
  foreignBeDomains: { [key in Locale]: string };
}

interface RouterContextActions {
  setPathname: (pathname: string) => void;
  getBeDomain: () => string;
  getProdBeDomain: (locale: Locale) => string;
  getProdDomain: (locale: Locale) => string;
}

type RouterContextType = RouterContextState & RouterContextActions;

const createRouterStore = (initialValues: Partial<RouterContextType> = {}) =>
  create<RouterContextType>((set, get) => ({
    protocol: initialValues.protocol || 'https',
    hostname: initialValues.hostname || '',
    pathname: initialValues.pathname || '',
    query: initialValues.query || '',
    goBack:
      initialValues.goBack || (() => console.log('goBack not implemented')),
    linkLegacyBehavior: initialValues.linkLegacyBehavior || false,
    prodBeDomains: {
      [Locale.Fr]: getBeDomain(Locale.Fr),
      [Locale.Nl]: getBeDomain(Locale.Nl),
      [Locale.En]: getBeDomain(Locale.En),
      [Locale.De]: getBeDomain(Locale.De),
    },
    foreignBeDomains: {
      [Locale.Fr]: getForeignDomain(Locale.Fr),
      [Locale.Nl]: getForeignDomain(Locale.Nl),
      [Locale.En]: getForeignDomain(Locale.En),
      [Locale.De]: getForeignDomain(Locale.De),
    },
    setPathname: (pathname: string) => set({ pathname }),
    getBeDomain: () => {
      const domain = `${get().protocol}://${get().hostname}`;
      const locale = Object.keys(get().prodBeDomains).find(
        (locale) =>
          domain.includes(get().prodBeDomains[locale as Locale]) ||
          domain.includes(get().foreignBeDomains[locale as Locale])
      );
      return locale ? get().prodBeDomains[locale as Locale] : '';
    },
    getProdBeDomain: (locale: Locale) => {
      return `${get().prodBeDomains[locale]}`;
    },
    getProdDomain: (locale: Locale) => {
      const domain = `${get().protocol}://${get().hostname}`;

      if (domain.match(/ardennes-etape\.(fr|co\.uk|de|nl)/)) {
        return get().foreignBeDomains[locale];
      }
      return get().prodBeDomains[locale];
    },
  }));

const RouterStoreContext = createContext<ReturnType<
  typeof createRouterStore
> | null>(null);

interface RouterProviderProps {
  protocol: string;
  hostname: string;
  pathname: string;
  query: string;
  goBack: () => void;
  linkLegacyBehavior?: boolean;
  children: ReactNode;
}

export const RouterProvider = ({
  protocol,
  hostname,
  pathname,
  query,
  goBack,
  linkLegacyBehavior = false,
  children,
}: RouterProviderProps) => {
  const store = useRef<ReturnType<typeof createRouterStore> | null>(null);

  if (!store.current) {
    store.current = createRouterStore({
      protocol,
      pathname,
      query,
      goBack,
      linkLegacyBehavior,
      hostname,
    });
  }

  useEffect(() => {
    store.current?.setState({
      protocol,
      pathname,
      query,
      hostname,
    });
  }, [pathname, hostname, protocol, query]);

  return (
    <RouterStoreContext.Provider value={store.current}>
      {children}
    </RouterStoreContext.Provider>
  );
};

const useRouterStore = <T,>(selector: (state: RouterContextType) => T): T => {
  const context = useContext(RouterStoreContext);
  if (!context) {
    throw new Error('useRouterStore must be used within a RouterProvider');
  }
  return useStore(context, useShallow(selector));
};

export const useDomain = (): string =>
  useRouterStore((state) => `${state.protocol}://${state.hostname}`);
export const useBeDomain = (): string =>
  useRouterStore((state) => state.getBeDomain());
export const useRequestUrl = (): string =>
  useRouterStore(
    (state) =>
      `${state.protocol}://${state.hostname}${Envs.basePath}${state.pathname}${state.query ? `?${state.query}` : ''}`
  );
export const useHostname = (): string =>
  useRouterStore((state) => state.hostname);
export const usePathname = (): string =>
  useRouterStore((state) => state.pathname);
export const useQuery = (): string => useRouterStore((state) => state.query);
export const useGoBack = (): (() => void) =>
  useRouterStore((state) => state.goBack);
export const useLinkLegacyBehavior = (): boolean =>
  useRouterStore((state) => state.linkLegacyBehavior);
export const useSetPathname = (): ((pathname: string) => void) =>
  useRouterStore((state) => state.setPathname);
export const useGetBeDomain = (): (() => string) =>
  useRouterStore((state) => state.getBeDomain);
export const useGetProdBeDomain = (): ((locale: Locale) => string) =>
  useRouterStore((state) => state.getProdBeDomain);
export const useGetProdDomain = (): ((locale: Locale) => string) =>
  useRouterStore((state) => state.getProdDomain);
